/**
 * Brands Page
 * Manage all brands with import, manual creation, and selection features
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { BrandSetupModal } from '../components/BrandSetupModal';
import { BrandLogo } from '../components/BrandLogo';
import { Icon } from '../components/icons';
import { clsx } from 'clsx';
import type { Brand, BrandDNA } from '../types';
import type { JonkaiBrandSchema } from '../types/brand-schema';
import {
  calculateBrandCompleteness,
  getCompletenessColor,
  type BrandCompletenessResult,
} from '../utils/brand-completeness';

// Brands will be fetched from API

type ViewMode = 'list' | 'create' | 'create-from-website' | 'import' | 'view' | 'edit';
type ImportFormat = 'json' | 'csv';

interface BrandExtractionResponse {
  success: boolean;
  error?: string;
  data?: {
    name: string;
    tagline: string;
    description: string;
    colors: { primary: string; secondary: string };
    tone: string[];
    keywords: string[];
    sourceUrl?: string;
    extractedColors?: string[];
    logoUrl?: string | null;
  };
}

export function Brands() {
  const navigate = useNavigate();
  const { selectedBrand, setSelectedBrand } = useBrand();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(true);
  const [brandDNAs, setBrandDNAs] = useState<Record<string, Partial<BrandDNA>>>({});
  const [brandCompleteness, setBrandCompleteness] = useState<Record<string, BrandCompletenessResult>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [importFormat, setImportFormat] = useState<ImportFormat>('json');
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [selectedBrandView, setSelectedBrandView] = useState<Brand | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Setup modal state
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupBrand, setSetupBrand] = useState<Brand | null>(null);

  // Edit brand state
  const [editBrand, setEditBrand] = useState<{
    name: string;
    tagline: string;
    description: string;
    website: string;
    logoUrl: string;
    colorPrimary: string;
    colorSecondary: string;
  }>({
    name: '',
    tagline: '',
    description: '',
    website: '',
    logoUrl: '',
    colorPrimary: '#8B5CF6',
    colorSecondary: '#6366F1',
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // New brand form state
  const [newBrand, setNewBrand] = useState({
    name: '',
    tagline: '',
    description: '',
    website: '',
    colorPrimary: '#8B5CF6',
    colorSecondary: '#6366F1',
  });

  // Website analysis state
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<{
    name: string;
    tagline: string;
    description: string;
    colors: { primary: string; secondary: string };
    tone: string[];
    keywords: string[];
    extractedColors?: string[];
    logoUrl?: string | null;
  } | null>(null);

  // Fetch brands from API on mount
  useEffect(() => {
    const fetchBrands = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setIsLoadingBrands(false);
        return;
      }

      try {
        const response = await fetch('/api/brands', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json() as { success: boolean; data?: Brand[] };
          if (data.success && data.data) {
            setBrands(data.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch brands:', error);
      } finally {
        setIsLoadingBrands(false);
      }
    };

    fetchBrands();
  }, []);

  // Calculate completeness for all brands when brands or DNAs change
  useEffect(() => {
    const completenessMap: Record<string, BrandCompletenessResult> = {};
    brands.forEach((brand) => {
      const dna = brandDNAs[brand.id] || null;
      completenessMap[brand.id] = calculateBrandCompleteness(brand, dna);
    });
    setBrandCompleteness(completenessMap);
  }, [brands, brandDNAs]);

  // API response types
  interface BrandDNAResponse {
    success: boolean;
    data?: Partial<BrandDNA>;
    error?: string;
  }

  // Fetch Brand DNA for all brands on mount
  useEffect(() => {
    const fetchBrandDNAs = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      for (const brand of brands) {
        try {
          const response = await fetch(`/api/brand-dna?brand_id=${brand.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data: BrandDNAResponse = await response.json();
            if (data.success && data.data) {
              setBrandDNAs((prev) => ({ ...prev, [brand.id]: data.data as Partial<BrandDNA> }));
            }
          }
        } catch (error) {
          console.error(`Failed to fetch DNA for brand ${brand.id}:`, error);
        }
      }
    };

    fetchBrandDNAs();
  }, [brands]);

  // Auto-trigger Brand DNA analysis for a brand
  const triggerBrandDNAAnalysis = async (brand: Brand): Promise<void> => {
    if (!brand.website_url) {
      throw new Error('No website URL provided');
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch('/api/brand-dna/analyze', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        brand_id: brand.id,
        website_url: brand.website_url,
      }),
    });

    const data: BrandDNAResponse = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Analysis failed');
    }

    // Update the brandDNAs state with new data
    if (data.data) {
      setBrandDNAs((prev) => ({ ...prev, [brand.id]: data.data as Partial<BrandDNA> }));
    }
  };

  // Handle brand creation completion - show setup modal
  const handleBrandCreated = (brand: Brand) => {
    setBrands((prev) => [...prev, brand]);
    setSetupBrand(brand);
    setShowSetupModal(true);
    setViewMode('list');

    // Auto-trigger analysis if website URL is present
    if (brand.website_url) {
      triggerBrandDNAAnalysis(brand).catch((error) => {
        console.error('Auto-analysis failed:', error);
      });
    }
  };

  // Filter brands based on search
  const filteredBrands = brands.filter(
    (brand) =>
      brand.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      brand.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle brand selection
  const handleSelectBrand = (brand: Brand) => {
    setSelectedBrand(brand);
  };

  // Handle JSON file import
  const handleFileImport = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setImportError(null);
      setImportSuccess(null);

      try {
        const text = await file.text();

        if (importFormat === 'json') {
          const data = JSON.parse(text) as JonkaiBrandSchema;

          // Validate schema version
          if (data.schemaVersion !== '2.0') {
            throw new Error(
              'Invalid schema version. Please use a schema version 2.0 JSON file.'
            );
          }

          // Create brand from imported data
          const newBrandData: Brand = {
            id: crypto.randomUUID(),
            name: data.brand.name,
            slug: data.brand.name.toLowerCase().replace(/\s+/g, '-'),
            j2k2_brand_id: null,
            logo_url: data.brand.logo || null,
            color_primary: data.brand.colors.primary,
            color_secondary: data.brand.colors.secondary,
            description: data.brand.tagline || data.brand.description || null,
            website_url: data.brand.website || null,
            created_at: Date.now(),
            updated_at: Date.now(),
          };

          setBrands((prev) => [...prev, newBrandData]);
          setImportSuccess(
            `Successfully imported "${data.brand.name}" brand!`
          );
          setViewMode('list');
        } else {
          // CSV import - parse rows
          const lines = text.split('\n').filter((line) => line.trim());
          if (lines.length < 2) {
            throw new Error('CSV must have a header row and at least one data row');
          }

          const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
          const requiredHeaders = ['name', 'color_primary'];
          const missingHeaders = requiredHeaders.filter(
            (h) => !headers.includes(h)
          );

          if (missingHeaders.length > 0) {
            throw new Error(
              `Missing required CSV columns: ${missingHeaders.join(', ')}`
            );
          }

          const importedBrands: Brand[] = [];

          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map((v) => v.trim());
            const row: Record<string, string> = {};
            headers.forEach((h, idx) => {
              row[h] = values[idx] || '';
            });

            if (row.name) {
              importedBrands.push({
                id: crypto.randomUUID(),
                name: row.name,
                slug: row.name.toLowerCase().replace(/\s+/g, '-'),
                j2k2_brand_id: null,
                logo_url: row.logo_url || null,
                color_primary: row.color_primary || '#8B5CF6',
                color_secondary: row.color_secondary || '#6366F1',
                description: row.description || row.tagline || null,
                website_url: row.website || row.website_url || null,
                created_at: Date.now(),
                updated_at: Date.now(),
              });
            }
          }

          if (importedBrands.length === 0) {
            throw new Error('No valid brands found in CSV');
          }

          setBrands((prev) => [...prev, ...importedBrands]);
          setImportSuccess(
            `Successfully imported ${importedBrands.length} brand(s)!`
          );
          setViewMode('list');
        }
      } catch (error) {
        setImportError(
          error instanceof Error ? error.message : 'Failed to import file'
        );
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [importFormat]
  );

  // Handle manual brand creation
  const handleCreateBrand = () => {
    if (!newBrand.name.trim()) {
      return;
    }

    const brandData: Brand = {
      id: crypto.randomUUID(),
      name: newBrand.name,
      slug: newBrand.name.toLowerCase().replace(/\s+/g, '-'),
      j2k2_brand_id: null,
      logo_url: null,
      color_primary: newBrand.colorPrimary,
      color_secondary: newBrand.colorSecondary,
      description: newBrand.tagline || newBrand.description || null,
      website_url: newBrand.website || null,
      created_at: Date.now(),
      updated_at: Date.now(),
    };

    // Use handleBrandCreated to show setup modal and auto-trigger analysis
    handleBrandCreated(brandData);

    // Reset form
    setNewBrand({
      name: '',
      tagline: '',
      description: '',
      website: '',
      colorPrimary: '#8B5CF6',
      colorSecondary: '#6366F1',
    });
  };

  // Download template
  const handleDownloadTemplate = (format: 'json' | 'csv') => {
    if (format === 'json') {
      // Download from public templates folder
      window.open('/templates/brand-template.json', '_blank');
    } else {
      // Generate CSV template
      const csvContent =
        'name,tagline,description,website,color_primary,color_secondary\n' +
        'Example Brand,Your Brand Tagline,A description of your brand,https://example.com,#8B5CF6,#6366F1';

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'brand-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // Delete brand
  const handleDeleteBrand = (brandId: string) => {
    if (confirm('Are you sure you want to delete this brand?')) {
      setBrands((prev) => prev.filter((b) => b.id !== brandId));
      if (selectedBrand?.id === brandId) {
        setSelectedBrand(null);
      }
    }
  };

  // Start editing a brand
  const handleStartEdit = (brand: Brand) => {
    setEditBrand({
      name: brand.name,
      tagline: '',
      description: brand.description || '',
      website: brand.website_url || '',
      logoUrl: brand.logo_url || '',
      colorPrimary: brand.color_primary,
      colorSecondary: brand.color_secondary,
    });
    setSelectedBrandView(brand);
    setViewMode('edit');
  };

  // Update brand
  const handleUpdateBrand = async () => {
    if (!selectedBrandView || !editBrand.name.trim()) return;

    setIsUpdating(true);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`/api/brands/${selectedBrandView.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editBrand.name,
          description: editBrand.description || editBrand.tagline,
          website_url: editBrand.website || null,
          logo_url: editBrand.logoUrl || null,
          color_primary: editBrand.colorPrimary,
          color_secondary: editBrand.colorSecondary,
        }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (response.ok && data.success) {
        // Update local state
        const updatedBrand: Brand = {
          ...selectedBrandView,
          name: editBrand.name,
          description: editBrand.description || editBrand.tagline || null,
          website_url: editBrand.website || null,
          logo_url: editBrand.logoUrl || null,
          color_primary: editBrand.colorPrimary,
          color_secondary: editBrand.colorSecondary,
          updated_at: Date.now(),
        };

        setBrands((prev) =>
          prev.map((b) => (b.id === selectedBrandView.id ? updatedBrand : b))
        );

        if (selectedBrand?.id === selectedBrandView.id) {
          setSelectedBrand(updatedBrand);
        }

        setImportSuccess(`Brand "${editBrand.name}" updated successfully!`);
        setViewMode('list');
      } else {
        throw new Error(data.error || 'Failed to update brand');
      }
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'Failed to update brand');
    } finally {
      setIsUpdating(false);
    }
  };

  // Analyze website to extract brand info
  const handleAnalyzeWebsite = async () => {
    if (!websiteUrl.trim()) return;

    setIsAnalyzing(true);
    setAnalysisError(null);
    setAnalysisResult(null);

    try {
      // Normalize URL
      let url = websiteUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }

      // Get auth token from localStorage
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Please log in to analyze websites');
      }

      // Call the real API endpoint
      const response = await fetch('/api/brands/extract-from-website', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ website_url: url }),
      });

      const data: BrandExtractionResponse = await response.json();

      if (!response.ok || !data.success || !data.data) {
        throw new Error(data.error || 'Failed to analyze website');
      }

      const result = data.data;

      setAnalysisResult({
        name: result.name,
        tagline: result.tagline,
        description: result.description,
        colors: result.colors,
        tone: result.tone,
        keywords: result.keywords,
        extractedColors: result.extractedColors,
        logoUrl: result.logoUrl,
      });

      // Pre-fill the form with analyzed data
      setNewBrand({
        name: result.name,
        tagline: result.tagline,
        description: result.description,
        website: result.sourceUrl || url,
        colorPrimary: result.colors.primary,
        colorSecondary: result.colors.secondary,
      });

    } catch (error) {
      setAnalysisError(
        error instanceof Error ? error.message : 'Failed to analyze website'
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Create brand from website analysis
  const handleCreateFromWebsite = () => {
    if (!newBrand.name.trim()) return;

    const brandData: Brand = {
      id: crypto.randomUUID(),
      name: newBrand.name,
      slug: newBrand.name.toLowerCase().replace(/\s+/g, '-'),
      j2k2_brand_id: null,
      logo_url: analysisResult?.logoUrl || null,
      color_primary: newBrand.colorPrimary,
      color_secondary: newBrand.colorSecondary,
      description: newBrand.tagline || newBrand.description || null,
      website_url: newBrand.website || null,
      created_at: Date.now(),
      updated_at: Date.now(),
    };

    // Use handleBrandCreated to show setup modal and auto-trigger full DNA analysis
    handleBrandCreated(brandData);

    // Reset state
    setNewBrand({
      name: '',
      tagline: '',
      description: '',
      website: '',
      colorPrimary: '#8B5CF6',
      colorSecondary: '#6366F1',
    });
    setWebsiteUrl('');
    setAnalysisResult(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Brands</h1>
          <p className="text-gray-600 mt-1">
            Manage your brands and their identity
          </p>
        </div>

        {viewMode === 'list' && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setViewMode('import')}
            >
              <Icon name="upload" size="sm" className="mr-2" />
              Import
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setWebsiteUrl('');
                setAnalysisResult(null);
                setAnalysisError(null);
                setViewMode('create-from-website');
              }}
            >
              <Icon name="sparkles" size="sm" className="mr-2" />
              From Website
            </Button>
            <Button onClick={() => setViewMode('create')}>
              <Icon name="plus" size="sm" className="mr-2" />
              New Brand
            </Button>
          </div>
        )}

        {viewMode !== 'list' && (
          <Button variant="outline" onClick={() => setViewMode('list')}>
            <Icon name="x" size="sm" className="mr-2" />
            Cancel
          </Button>
        )}
      </div>

      {/* Success/Error Messages */}
      {importSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Icon name="check" size="md" />
          {importSuccess}
        </div>
      )}

      {importError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Icon name="alert" size="md" />
          {importError}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Search */}
          <div className="relative">
            <Icon name="search" size="md" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          {/* Currently Selected */}
          {selectedBrand && (
            <Card className="border-purple-200 bg-purple-50/50">
              <div className="flex items-center gap-4">
                <BrandLogo
                  logoUrl={selectedBrand.logo_url}
                  brandName={selectedBrand.name}
                  primaryColor={selectedBrand.color_primary}
                  size="lg"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">
                      {selectedBrand.name}
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                      Active
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">
                    {selectedBrand.description}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Brands Grid */}
          {isLoadingBrands ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="loader" size="xl" className="animate-spin text-purple-600" />
              <span className="ml-2 text-gray-600">Loading brands...</span>
            </div>
          ) : filteredBrands.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="brands" size="3xl" className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No brands found</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setViewMode('create')}
              >
                <Icon name="plus" size="sm" className="mr-2" />
                Create Your First Brand
              </Button>
            </div>
          ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredBrands.map((brand) => (
              <Card
                key={brand.id}
                padding="none"
                className={clsx(
                  'overflow-hidden transition-all hover:shadow-md cursor-pointer',
                  selectedBrand?.id === brand.id && 'ring-2 ring-purple-500'
                )}
              >
                {/* Color Banner */}
                <div
                  className="h-2"
                  style={{
                    background: `linear-gradient(90deg, ${brand.color_primary}, ${brand.color_secondary})`,
                  }}
                />

                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <BrandLogo
                        logoUrl={brand.logo_url}
                        brandName={brand.name}
                        primaryColor={brand.color_primary}
                        size="md"
                      />
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {brand.name}
                        </h3>
                        <p className="text-sm text-gray-500 truncate">
                          {brand.description || 'No description'}
                        </p>
                      </div>
                    </div>

                    {selectedBrand?.id === brand.id && (
                      <Icon name="check" size="md" className="text-purple-600 flex-shrink-0" />
                    )}
                  </div>

                  {/* Completeness Indicator */}
                  {brandCompleteness[brand.id] && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-500">Profile</span>
                        <span
                          className="font-medium"
                          style={{ color: getCompletenessColor(brandCompleteness[brand.id].percentage) }}
                        >
                          {brandCompleteness[brand.id].percentage}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${brandCompleteness[brand.id].percentage}%`,
                            backgroundColor: getCompletenessColor(brandCompleteness[brand.id].percentage),
                          }}
                        />
                      </div>
                      {brandCompleteness[brand.id].percentage < 60 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSetupBrand(brand);
                            setShowSetupModal(true);
                          }}
                          className="mt-2 text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                        >
                          <Icon name="trending-up" size="xs" />
                          Complete Setup
                        </button>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                    <Button
                      variant={selectedBrand?.id === brand.id ? 'secondary' : 'primary'}
                      size="sm"
                      className="flex-1"
                      onClick={() => handleSelectBrand(brand)}
                    >
                      {selectedBrand?.id === brand.id ? 'Selected' : 'Select'}
                    </Button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBrandView(brand);
                        setViewMode('view');
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="View details"
                    >
                      <Icon name="eye" size="sm" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEdit(brand);
                      }}
                      className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                      title="Edit brand"
                    >
                      <Icon name="edit" size="sm" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteBrand(brand.id);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete brand"
                    >
                      <Icon name="trash" size="sm" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          )}
        </>
      )}

      {/* Create Brand View */}
      {viewMode === 'create' && (
        <Card>
          <CardHeader
            title="Create New Brand"
            subtitle="Add a new brand manually"
          />

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Brand Name *"
                value={newBrand.name}
                onChange={(e) =>
                  setNewBrand((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., My Awesome Brand"
              />
              <Input
                label="Tagline"
                value={newBrand.tagline}
                onChange={(e) =>
                  setNewBrand((prev) => ({ ...prev, tagline: e.target.value }))
                }
                placeholder="e.g., Making awesome things"
              />
            </div>

            <Textarea
              label="Description"
              value={newBrand.description}
              onChange={(e) =>
                setNewBrand((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Describe your brand..."
              rows={3}
            />

            <Input
              label="Website URL"
              type="url"
              value={newBrand.website}
              onChange={(e) =>
                setNewBrand((prev) => ({ ...prev, website: e.target.value }))
              }
              placeholder="https://example.com"
            />

            {/* Colors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand Colors
              </label>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={newBrand.colorPrimary}
                    onChange={(e) =>
                      setNewBrand((prev) => ({
                        ...prev,
                        colorPrimary: e.target.value,
                      }))
                    }
                    className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                  />
                  <div>
                    <span className="text-sm text-gray-600">Primary</span>
                    <p className="text-xs text-gray-400">
                      {newBrand.colorPrimary}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={newBrand.colorSecondary}
                    onChange={(e) =>
                      setNewBrand((prev) => ({
                        ...prev,
                        colorSecondary: e.target.value,
                      }))
                    }
                    className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                  />
                  <div>
                    <span className="text-sm text-gray-600">Secondary</span>
                    <p className="text-xs text-gray-400">
                      {newBrand.colorSecondary}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview
              </label>
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                    style={{ backgroundColor: newBrand.colorPrimary }}
                  >
                    {newBrand.name?.[0] || '?'}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {newBrand.name || 'Brand Name'}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {newBrand.tagline || 'Your tagline here'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setViewMode('list')}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateBrand}
                disabled={!newBrand.name.trim()}
              >
                <Icon name="plus" size="sm" className="mr-2" />
                Create Brand
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Create from Website View */}
      {viewMode === 'create-from-website' && (
        <div className="space-y-6">
          {/* Website URL Input */}
          <Card>
            <CardHeader
              title="Create Brand from Website"
              subtitle="Enter a website URL and we'll automatically extract brand information using AI"
            />

            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    label="Website URL"
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://example.com"
                    disabled={isAnalyzing}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleAnalyzeWebsite}
                    disabled={!websiteUrl.trim() || isAnalyzing}
                  >
                    {isAnalyzing ? (
                      <>
                        <Icon name="loader" size="sm" className="mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Icon name="sparkles" size="sm" className="mr-2" />
                        Analyze
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {analysisError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <Icon name="alert" size="md" className="flex-shrink-0" />
                  {analysisError}
                </div>
              )}
            </div>
          </Card>

          {/* Analysis Progress */}
          {isAnalyzing && (
            <Card className="bg-purple-50 border-purple-200">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Icon name="loader" size="lg" className="text-purple-600 animate-spin" />
                </div>
                <div>
                  <h4 className="font-medium text-purple-900">Analyzing Website</h4>
                  <p className="text-sm text-purple-700">
                    Extracting brand colors, tone, and identity...
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Analysis Results & Edit Form */}
          {analysisResult && !isAnalyzing && (
            <>
              {/* AI Detected Info */}
              <Card className="bg-green-50 border-green-200">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Icon name="check" size="md" className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-green-900">Analysis Complete</h4>
                    <p className="text-sm text-green-700 mt-1">
                      We detected the following brand attributes. Review and edit as needed.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {analysisResult.tone.map((t) => (
                        <span
                          key={t}
                          className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Editable Form */}
              <Card>
                <CardHeader
                  title="Review & Edit"
                  subtitle="Make any adjustments to the detected brand information"
                />

                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Input
                      label="Brand Name *"
                      value={newBrand.name}
                      onChange={(e) =>
                        setNewBrand((prev) => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="e.g., My Awesome Brand"
                    />
                    <Input
                      label="Tagline"
                      value={newBrand.tagline}
                      onChange={(e) =>
                        setNewBrand((prev) => ({ ...prev, tagline: e.target.value }))
                      }
                      placeholder="e.g., Making awesome things"
                    />
                  </div>

                  <Textarea
                    label="Description"
                    value={newBrand.description}
                    onChange={(e) =>
                      setNewBrand((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Describe your brand..."
                    rows={3}
                  />

                  <Input
                    label="Website URL"
                    type="url"
                    value={newBrand.website}
                    onChange={(e) =>
                      setNewBrand((prev) => ({ ...prev, website: e.target.value }))
                    }
                    placeholder="https://example.com"
                    disabled
                  />

                  {/* Colors */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detected Brand Colors
                    </label>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={newBrand.colorPrimary}
                          onChange={(e) =>
                            setNewBrand((prev) => ({
                              ...prev,
                              colorPrimary: e.target.value,
                            }))
                          }
                          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                        />
                        <div>
                          <span className="text-sm text-gray-600">Primary</span>
                          <p className="text-xs text-gray-400">
                            {newBrand.colorPrimary}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={newBrand.colorSecondary}
                          onChange={(e) =>
                            setNewBrand((prev) => ({
                              ...prev,
                              colorSecondary: e.target.value,
                            }))
                          }
                          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                        />
                        <div>
                          <span className="text-sm text-gray-600">Secondary</span>
                          <p className="text-xs text-gray-400">
                            {newBrand.colorSecondary}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Extracted Colors (clickable to select) */}
                  {analysisResult.extractedColors && analysisResult.extractedColors.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Colors Found on Website
                        <span className="text-xs text-gray-500 font-normal ml-2">
                          (click to use)
                        </span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {analysisResult.extractedColors.map((color) => (
                          <button
                            key={color}
                            onClick={() => setNewBrand((prev) => ({ ...prev, colorPrimary: color }))}
                            className={clsx(
                              'w-10 h-10 rounded-lg border-2 transition-all hover:scale-110',
                              newBrand.colorPrimary.toLowerCase() === color.toLowerCase()
                                ? 'border-purple-500 ring-2 ring-purple-200'
                                : 'border-gray-200 hover:border-gray-300'
                            )}
                            style={{ backgroundColor: color }}
                            title={`Use ${color} as primary color`}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Keywords */}
                  {analysisResult.keywords.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Detected Keywords
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {analysisResult.keywords.map((keyword) => (
                          <span
                            key={keyword}
                            className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-700"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preview */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preview
                    </label>
                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-3">
                        <BrandLogo
                          logoUrl={analysisResult?.logoUrl}
                          brandName={newBrand.name || 'Brand'}
                          primaryColor={newBrand.colorPrimary}
                          size="lg"
                        />
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {newBrand.name || 'Brand Name'}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {newBrand.tagline || 'Your tagline here'}
                          </p>
                        </div>
                      </div>
                      {analysisResult?.logoUrl && (
                        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                          <Icon name="check" size="xs" />
                          Logo detected from website
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button variant="outline" onClick={() => setViewMode('list')}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateFromWebsite}
                      disabled={!newBrand.name.trim()}
                    >
                      <Icon name="sparkles" size="sm" className="mr-2" />
                      Create Brand
                    </Button>
                  </div>
                </div>
              </Card>
            </>
          )}

          {/* Info Card */}
          {!analysisResult && !isAnalyzing && (
            <Card className="bg-blue-50 border-blue-200">
              <div className="flex gap-3">
                <Icon name="sparkles" size="md" className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">AI-Powered Brand Extraction</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Our AI will analyze your website to extract brand colors, tone of voice,
                    key messaging, and more. This information will be used to generate
                    on-brand content across all your marketing channels.
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Import View */}
      {viewMode === 'import' && (
        <div className="space-y-6">
          {/* Format Selection */}
          <Card>
            <CardHeader
              title="Import Brands"
              subtitle="Import brands from JSON or CSV files"
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <button
                onClick={() => setImportFormat('json')}
                className={clsx(
                  'p-4 rounded-lg border-2 text-left transition-all',
                  importFormat === 'json'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={clsx(
                      'p-2 rounded-lg',
                      importFormat === 'json'
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-gray-100 text-gray-600'
                    )}
                  >
                    <Icon name="file-text" size="lg" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">JSON Format</h4>
                    <p className="text-sm text-gray-500">
                      Full brand schema with all details
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setImportFormat('csv')}
                className={clsx(
                  'p-4 rounded-lg border-2 text-left transition-all',
                  importFormat === 'csv'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={clsx(
                      'p-2 rounded-lg',
                      importFormat === 'csv'
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-gray-100 text-gray-600'
                    )}
                  >
                    <Icon name="layers" size="lg" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">CSV Format</h4>
                    <p className="text-sm text-gray-500">
                      Bulk import multiple brands
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </Card>

          {/* Upload Area */}
          <Card>
            <CardHeader
              title={`Upload ${importFormat.toUpperCase()} File`}
              subtitle={
                importFormat === 'json'
                  ? 'Upload a JSON file following the Jonkai brand schema'
                  : 'Upload a CSV file with brand information'
              }
            />

            <input
              ref={fileInputRef}
              type="file"
              accept={importFormat === 'json' ? '.json' : '.csv'}
              onChange={handleFileImport}
              className="hidden"
            />

            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors"
            >
              <Icon name="upload" size="3xl" className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-700 font-medium">
                Click to upload or drag and drop
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {importFormat === 'json' ? '.json files' : '.csv files'}
              </p>
            </div>
          </Card>

          {/* Download Templates */}
          <Card>
            <CardHeader
              title="Download Templates"
              subtitle="Get template files to fill in your brand information"
            />

            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => handleDownloadTemplate('json')}
              >
                <Icon name="download" size="sm" className="mr-2" />
                JSON Template
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDownloadTemplate('csv')}
              >
                <Icon name="download" size="sm" className="mr-2" />
                CSV Template
              </Button>
            </div>
          </Card>

          {/* Schema Info */}
          <Card className="bg-blue-50 border-blue-200">
            <div className="flex gap-3">
              <Icon name="alert" size="md" className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">JSON Schema v2.0</h4>
                <p className="text-sm text-blue-700 mt-1">
                  The JSON format supports the full brand schema including
                  mission, tone of voice, vocabulary, competitors, and more. This
                  enables AI-powered content generation that truly matches your
                  brand.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* View Brand Details */}
      {viewMode === 'view' && selectedBrandView && (
        <Card>
          <CardHeader
            title={selectedBrandView.name}
            subtitle={selectedBrandView.description || 'No description'}
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <Icon name="x" size="sm" className="mr-2" />
                Close
              </Button>
            }
          />

          <div className="space-y-6">
            {/* Color Banner */}
            <div
              className="h-24 rounded-lg"
              style={{
                background: `linear-gradient(135deg, ${selectedBrandView.color_primary}, ${selectedBrandView.color_secondary})`,
              }}
            />

            {/* Details Grid */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <Icon name="palette" size="sm" />
                  <span className="text-sm">Primary Color</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded"
                    style={{ backgroundColor: selectedBrandView.color_primary }}
                  />
                  <span className="font-mono text-gray-900">
                    {selectedBrandView.color_primary}
                  </span>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <Icon name="palette" size="sm" />
                  <span className="text-sm">Secondary Color</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded"
                    style={{ backgroundColor: selectedBrandView.color_secondary }}
                  />
                  <span className="font-mono text-gray-900">
                    {selectedBrandView.color_secondary}
                  </span>
                </div>
              </div>

              {selectedBrandView.website_url && (
                <div className="p-4 bg-gray-50 rounded-lg sm:col-span-2">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Icon name="globe" size="sm" />
                    <span className="text-sm">Website</span>
                  </div>
                  <a
                    href={selectedBrandView.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 flex items-center gap-1"
                  >
                    {selectedBrandView.website_url}
                    <Icon name="arrow-right" size="sm" />
                  </a>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              <Button
                onClick={() => {
                  handleSelectBrand(selectedBrandView);
                  setViewMode('list');
                }}
              >
                <Icon name="check" size="sm" className="mr-2" />
                Select This Brand
              </Button>
              <Button
                variant="outline"
                onClick={() => handleStartEdit(selectedBrandView)}
              >
                <Icon name="edit" size="sm" className="mr-2" />
                Edit Brand
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Edit Brand View */}
      {viewMode === 'edit' && selectedBrandView && (
        <Card>
          <CardHeader
            title="Edit Brand"
            subtitle={`Editing ${selectedBrandView.name}`}
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <Icon name="x" size="sm" className="mr-2" />
                Cancel
              </Button>
            }
          />

          <div className="space-y-6">
            {/* Logo Section */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Brand Logo
              </label>
              <div className="flex items-start gap-4">
                <BrandLogo
                  logoUrl={editBrand.logoUrl || null}
                  brandName={editBrand.name || 'Brand'}
                  primaryColor={editBrand.colorPrimary}
                  size="xl"
                />
                <div className="flex-1">
                  <Input
                    label="Logo URL"
                    type="url"
                    value={editBrand.logoUrl}
                    onChange={(e) =>
                      setEditBrand((prev) => ({ ...prev, logoUrl: e.target.value }))
                    }
                    placeholder="https://example.com/logo.png"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter a direct URL to your brand logo image (PNG, JPG, SVG)
                  </p>
                </div>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Brand Name *"
                value={editBrand.name}
                onChange={(e) =>
                  setEditBrand((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., My Awesome Brand"
              />
              <Input
                label="Tagline"
                value={editBrand.tagline}
                onChange={(e) =>
                  setEditBrand((prev) => ({ ...prev, tagline: e.target.value }))
                }
                placeholder="e.g., Making awesome things"
              />
            </div>

            <Textarea
              label="Description"
              value={editBrand.description}
              onChange={(e) =>
                setEditBrand((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Describe your brand..."
              rows={3}
            />

            <Input
              label="Website URL"
              type="url"
              value={editBrand.website}
              onChange={(e) =>
                setEditBrand((prev) => ({ ...prev, website: e.target.value }))
              }
              placeholder="https://example.com"
            />

            {/* Colors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand Colors
              </label>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editBrand.colorPrimary}
                    onChange={(e) =>
                      setEditBrand((prev) => ({
                        ...prev,
                        colorPrimary: e.target.value,
                      }))
                    }
                    className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                  />
                  <div>
                    <span className="text-sm text-gray-600">Primary</span>
                    <p className="text-xs text-gray-400">
                      {editBrand.colorPrimary}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editBrand.colorSecondary}
                    onChange={(e) =>
                      setEditBrand((prev) => ({
                        ...prev,
                        colorSecondary: e.target.value,
                      }))
                    }
                    className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                  />
                  <div>
                    <span className="text-sm text-gray-600">Secondary</span>
                    <p className="text-xs text-gray-400">
                      {editBrand.colorSecondary}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview
              </label>
              <div className="p-4 border border-gray-200 rounded-lg bg-white">
                <div
                  className="h-2 rounded-t-lg -mx-4 -mt-4 mb-4"
                  style={{
                    background: `linear-gradient(90deg, ${editBrand.colorPrimary}, ${editBrand.colorSecondary})`,
                  }}
                />
                <div className="flex items-center gap-3">
                  <BrandLogo
                    logoUrl={editBrand.logoUrl || null}
                    brandName={editBrand.name || 'Brand'}
                    primaryColor={editBrand.colorPrimary}
                    size="lg"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {editBrand.name || 'Brand Name'}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {editBrand.tagline || editBrand.description || 'Your tagline here'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setViewMode('list')}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateBrand}
                disabled={!editBrand.name.trim() || isUpdating}
              >
                {isUpdating ? (
                  <>
                    <Icon name="loader" size="sm" className="mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icon name="save" size="sm" className="mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Brand Setup Modal */}
      {showSetupModal && setupBrand && brandCompleteness[setupBrand.id] && (
        <BrandSetupModal
          brand={setupBrand}
          missingFields={brandCompleteness[setupBrand.id].missingFields}
          completenessPercentage={brandCompleteness[setupBrand.id].percentage}
          onClose={() => {
            setShowSetupModal(false);
            setSetupBrand(null);
          }}
          onStartAnalysis={async (websiteUrl) => {
            // Update brand with website URL if not set
            if (!setupBrand.website_url) {
              const updatedBrand = { ...setupBrand, website_url: websiteUrl };
              setBrands((prev) =>
                prev.map((b) => (b.id === setupBrand.id ? updatedBrand : b))
              );
              setSetupBrand(updatedBrand);
            }
            // Trigger DNA analysis
            await triggerBrandDNAAnalysis({
              ...setupBrand,
              website_url: websiteUrl,
            });
          }}
          onSkip={() => {
            setShowSetupModal(false);
            setSetupBrand(null);
            setImportSuccess(`Brand "${setupBrand.name}" created! You can complete the setup later.`);
          }}
          onNavigateToDNA={() => {
            setShowSetupModal(false);
            setSetupBrand(null);
            // Navigate to Brand DNA page with the brand pre-selected
            setSelectedBrand(setupBrand);
            navigate('/brand-dna');
          }}
        />
      )}
    </div>
  );
}
